/**
 * useMediaGallery — Extracts all media items from messages for gallery navigation.
 *
 * Returns a flat array of media items (images, videos, PDFs, files) in chronological
 * order, and a helper to find the index of a given message.
 */

import { useMemo, useCallback } from 'react';
import type { ChatMessage } from '../types';

export interface MediaItem {
  messageId: string;
  type: 'image' | 'video' | 'pdf' | 'file';
  url: string;
  thumbnail?: string | null;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  senderName: string;
  timestamp: string;
}

function isPdf(msg: ChatMessage): boolean {
  const meta = msg.mediaMetadata as { mimeType?: string; fileName?: string } | null;
  if (meta?.mimeType === 'application/pdf') return true;
  if (meta?.fileName?.toLowerCase().endsWith('.pdf')) return true;
  return false;
}

export function useMediaGallery(messages: ChatMessage[]) {
  const items = useMemo<MediaItem[]>(() => {
    return messages
      .filter((msg) => ['image', 'video', 'file'].includes(msg.type) && msg.mediaUrl && !msg.isDeleted)
      .map((msg) => {
        const meta = msg.mediaMetadata as {
          fileName?: string;
          fileSize?: number;
          mimeType?: string;
        } | null;

        let type: MediaItem['type'];
        if (msg.type === 'image') type = 'image';
        else if (msg.type === 'video') type = 'video';
        else if (msg.type === 'file' && isPdf(msg)) type = 'pdf';
        else type = 'file';

        return {
          messageId: msg.id,
          type,
          url: msg.mediaUrl!,
          thumbnail: msg.mediaThumbnail,
          fileName: meta?.fileName,
          fileSize: meta?.fileSize,
          mimeType: meta?.mimeType,
          senderName: msg.sender.name || '',
          timestamp: msg.createdAt,
        };
      });
  }, [messages]);

  const findIndex = useCallback(
    (messageId: string) => items.findIndex((item) => item.messageId === messageId),
    [items]
  );

  return { items, findIndex };
}
